import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { fetchAddresses, fetchSiteSettings, fetchMenuItems } from '../lib/api.js';

const FB = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 21v-7.5h2.5l.4-3h-2.9V8.6c0-.9.3-1.5 1.6-1.5H17V4.4c-.3 0-1.3-.1-2.4-.1-2.4 0-4.1 1.5-4.1 4.2v2H8v3h2.5V21z"/></svg>';
const YT = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M21.6 7.2c-.2-.9-.9-1.6-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4c-.9.2-1.6.9-1.8 1.8C2 8.8 2 12 2 12s0 3.2.4 4.8c.2.9.9 1.6 1.8 1.8 1.6.4 7.8.4 7.8.4s6.2 0 7.8-.4c.9-.2 1.6-.9 1.8-1.8.4-1.6.4-4.8.4-4.8s0-3.2-.4-4.8zM10 15V9l5.2 3z"/></svg>';
const IG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>';
const LI = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S.02 4.88.02 3.5C.02 2.12 1.13 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8h4V21h-4zM8.5 8h3.84v1.78h.05c.53-.95 1.85-1.95 3.81-1.95 4.08 0 4.83 2.55 4.83 5.87V21h-4v-5.78c0-1.38-.03-3.16-1.93-3.16-1.94 0-2.24 1.5-2.24 3.06V21h-4z"/></svg>';

const Icon = ({ html }) => <span dangerouslySetInnerHTML={{ __html: html }} />;

const isInternalUrl = (u) => !!u && u.startsWith('/') && !u.endsWith('.xml');

/**
 * Render a menu item — internal links use react-router-dom Link;
 * external links / files (.xml, http(s)) use a plain <a>.
 */
function MenuLink({ item }) {
  if (isInternalUrl(item.url)) {
    return <Link to={item.url}>{item.label}</Link>;
  }
  return (
    <a
      href={item.url}
      target={item.openInNewTab ? '_blank' : undefined}
      rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
    >
      {item.label}
    </a>
  );
}

// Hardcoded fallbacks — only used while the API call is in flight
// or if the school deletes a menu location entirely.
const FALLBACK_QUICK = [
  { id: 'fq1', label: 'Home', url: '/' },
  { id: 'fq2', label: 'About Us', url: '/about' },
  { id: 'fq3', label: 'Events', url: '/events' },
  { id: 'fq4', label: 'News Feed', url: '/news' },
  { id: 'fq5', label: 'Admission', url: '/admission' },
  { id: 'fq6', label: 'Alumni', url: '/alumni' },
];
const FALLBACK_RESOURCES = [
  { id: 'fr1', label: 'Career', url: '/career' },
  { id: 'fr2', label: 'IGCSE & IAL Info', url: '/page/igcse-ial-info' },
  { id: 'fr3', label: 'School Rules', url: '/page/school-rules' },
  { id: 'fr4', label: 'FAQs', url: '/page/faqs' },
  { id: 'fr5', label: 'Academic Calendar', url: '/calendar' },
];
const FALLBACK_BOTTOM = [
  { id: 'fb1', label: 'Privacy', url: '/page/privacy' },
  { id: 'fb2', label: 'Terms', url: '/page/terms' },
  { id: 'fb3', label: 'Sitemap', url: '/sitemap.xml' },
];

export function Footer() {
  const { data: settings } = useApi(fetchSiteSettings, []);
  const { data: addresses } = useApi(fetchAddresses, []);
  const { data: menu } = useApi(fetchMenuItems, []);

  const offices = (addresses || []).filter(
    (a) => a.kind === 'admission_office_1' || a.kind === 'admission_office_2',
  );

  const quick     = (menu?.footer_quick     && menu.footer_quick.length)     ? menu.footer_quick     : FALLBACK_QUICK;
  const resources = (menu?.footer_resources && menu.footer_resources.length) ? menu.footer_resources : FALLBACK_RESOURCES;
  const bottom    = (menu?.footer_bottom    && menu.footer_bottom.length)    ? menu.footer_bottom    : FALLBACK_BOTTOM;

  return (
    <footer className="site-footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <Link to="/" className="brand">
              <div className="brand-mark">
                <img
                  src={settings?.logo || '/img/mlis-logo.jpg'}
                  alt={settings?.schoolName || 'Maple Leaf International School'}
                  width="48" height="48" loading="lazy" decoding="async"
                />
              </div>
              <div className="brand-text">
                <span className="brand-name" style={{ color: '#fff' }}>
                  {settings?.schoolName || 'Maple Leaf'}
                </span>
                <span className="brand-tag">International School</span>
              </div>
            </Link>
            <p style={{ marginTop: 22, maxWidth: '36ch' }}>
              {settings?.footerText || 'Each of our students may take different routes on their educational journey, but our aim remains constant for all — to enlighten the path ahead.'}
            </p>
            <div className="social">
              {settings?.facebookUrl  && <a href={settings.facebookUrl}  aria-label="Facebook"><Icon html={FB} /></a>}
              {settings?.youtubeUrl   && <a href={settings.youtubeUrl}   aria-label="YouTube"><Icon html={YT} /></a>}
              {settings?.instagramUrl && <a href={settings.instagramUrl} aria-label="Instagram"><Icon html={IG} /></a>}
              {settings?.linkedinUrl  && <a href={settings.linkedinUrl}  aria-label="LinkedIn"><Icon html={LI} /></a>}
              {!settings?.facebookUrl && !settings?.instagramUrl && (
                <>
                  <a href="#" aria-label="Facebook"><Icon html={FB} /></a>
                  <a href="#" aria-label="YouTube"><Icon html={YT} /></a>
                  <a href="#" aria-label="Instagram"><Icon html={IG} /></a>
                </>
              )}
            </div>
          </div>

          <div>
            <h4>Quick Menu</h4>
            <ul className="footer-links">
              {quick.map((it) => (
                <li key={it.id}><MenuLink item={it} /></li>
              ))}
            </ul>
          </div>

          <div>
            <h4>Resources</h4>
            <ul className="footer-links">
              {resources.map((it) => (
                <li key={it.id}><MenuLink item={it} /></li>
              ))}
            </ul>
          </div>

          <div>
            <h4>Visit Us</h4>
            {offices.length > 0 ? offices.map((o) => (
              <div className="footer-info" key={o.id}>
                <strong>{o.title}</strong>
                {o.addressLine && <>{o.addressLine}<br /></>}
                {o.mobile && <span>{o.mobile}</span>}
              </div>
            )) : (
              <>
                <div className="footer-info">
                  <strong>Admission Office 1</strong>
                  Play Group to Class X<br />
                  House 31, Road 14/A, Dhanmondi, Dhaka
                </div>
                <div className="footer-info">
                  <strong>Admission Office 2</strong>
                  Classes XI–XII<br />
                  House 95/1, Road 11/A, Dhanmondi, Dhaka
                </div>
              </>
            )}
          </div>
        </div>

        <div className="footer-bottom">
          <span>{settings?.footerCopyright || `© Maple Leaf International School ${new Date().getFullYear()} — All Rights Reserved`}</span>
          <div className="footer-bottom-links">
            {bottom.map((it) => (
              <MenuLink item={it} key={it.id} />
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
